import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuthHeader } from '../utils/auth';
import { useOutletContext } from 'react-router-dom';
import { useBranding } from "../hooks/useBranding";
import { isEnglishOperatorMode } from '../utils/operatorMode';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

// --- Icons ---
const Icons = {
    Plus: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Edit: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
    Trash: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Eye: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
    Up: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>,
    Down: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>,
    Code: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>,
    Settings: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
};

const FORMS_COPY = {
    ja: {
        pageTitle: 'フォーム管理',
        pageSubtitle: 'Marketing Lead Engine',
        createButton: '新規フォーム作成',
        freeLimit: '無料プランではフォームを1つまでしか作成できません。',
        loading: 'Syncing Data...',
        emptyTitle: 'フォームがまだありません',
        emptyBody: '顧客を受け取るための最初のエンジンの火を灯しましょう。',
        emptyCta: 'Initialize First System →',
        externalModeLabel: 'API Tunnel Enabled',
        unnamedForm: 'Unnamed Form',
        integration: 'Integration',
        deleteConfirm: 'このフォームを削除しますか？\n設置済みの箇所も表示されなくなります。',
        deleteFailed: '削除失敗',
        copied: 'コピーしました！',
        formEditor: {
            editTitle: 'フォームを編集',
            newTitle: '新規フォーム作成',
            oneTagMode: '外部連携 (One-Tag) モード',
            oneTagBody: 'ONにすると自社LPや外部サイトのフォームを自動検知します。デザイン設定は無効化されますが、自動返信やCAPIは動作します。',
            basic: '基本情報',
            internalName: '管理名称 (内部用)',
            internalPlaceholder: '例: 2026年2月キャンペーン',
            displayTitle: '表示タイトル (ユーザー用)',
            displayPlaceholder: '例: 資料請求・お問い合わせ',
            externalDisplayNote: '※外部サイト側には反映されませんが、プレビュー用として設定可能です。',
            email: 'メール配信・通知',
            autoReply: '自動返信メール',
            autoReplyNote: '外部サイトのフォームでも、入力されたメールアドレス宛に自動返信メールを送信します。',
            sender: '自動返信の送信元',
            systemSender: 'システム共通（自動返信用: mailer@sbroute.net）',
            subjectPlaceholder: '件名: お問い合わせありがとうございます',
            bodyPlaceholder: '本文: {{お名前}} 様...',
            notifyTo: '通知先アドレス (管理者用)',
            afterSubmit: '完了後の挙動',
            successMessage: '表示メッセージ',
            redirectUrl: 'リダイレクト先URL (任意)',
            redirectNote: '外部サイトでの送信後、自動でこのURLに転送します。',
            design: 'デザイン・外観',
            themeColor: 'テーマカラー',
            customColor: 'カスタムカラー (HEX)',
            buttonText: '送信ボタンのテキスト',
            buttonPlaceholder: '例: この内容で送信する',
            fields: '入力項目の構成',
            fieldLabel: '項目ラベル',
            placeholderLabel: 'プレースホルダー',
            selectPlaceholderLabel: '未選択時の表示',
            optionPlaceholder: '例: 選択してください',
            textPlaceholder: '例: 山田 太郎',
            options: '選択肢',
            applyFromPlaceholder: 'プレースホルダーから反映',
            delete: '削除',
            addOption: '+ 選択肢を追加',
            required: '必須項目にする',
            deleteFieldConfirm: 'この項目を削除しますか？',
            previewTitle: 'フォームタイトル',
            previewInputSuffix: 'を入力...',
            externalActive: '外部サイト連携中',
            cancel: 'キャンセル',
            save: '設定を保存して公開',
            submitSuccessDefault: 'お問い合わせありがとうございます。内容を確認の上、担当者よりご連絡いたします。',
            submitButtonDefault: 'この内容で送信する',
        },
        embed: {
            title: '設置・連携コード',
            subtitle: 'Deployment & Integration',
            tagTab: '専用タグ',
            linkTab: '専用URL',
            iframeTab: '埋め込み',
            capiTab: 'CAPI連携 (SDK)',
            tagDescription: 'このフォーム専用の自動検知タグです。外部サイト（WordPress等）の <head> 内に貼り付けてください。これを貼るだけで、そのサイト上のフォーム送信を検知し、このフォームの設定（自動返信など）が適用されます。',
            tagLabel: 'One-Tag with Specific Token',
            copyTag: 'Copy Dedicated Tag',
            linkLabel: 'Public Access URL',
            copy: 'Copy',
            iframeLabel: 'HTML Embed Code',
            copyCode: 'Copy Code',
            capiDescription: '自作LPのフォーム成功時にこのスクリプトを実行することで、データ同期と Meta CAPI の広告最適化が同時に行われます。',
            copySdk: 'Copy SDK Code',
            close: '閉じる'
        }
    },
    en: {
        pageTitle: 'Form Builder',
        pageSubtitle: 'Marketing Lead Engine',
        createButton: 'Create Form',
        freeLimit: 'Free plans can create one form only.',
        loading: 'Syncing Data...',
        emptyTitle: 'No forms yet',
        emptyBody: 'Create your first lead capture form for web, ad, and campaign traffic.',
        emptyCta: 'Create First Form →',
        externalModeLabel: 'API Tunnel Enabled',
        unnamedForm: 'Unnamed Form',
        integration: 'Integration',
        deleteConfirm: 'Delete this form? Any installed placements will stop displaying it.',
        deleteFailed: 'Delete failed',
        copied: 'Copied.',
        formEditor: {
            editTitle: 'Edit Form',
            newTitle: 'Create Form',
            oneTagMode: 'External One-Tag Mode',
            oneTagBody: 'Detect submissions from forms on your website or landing pages. Design settings are disabled in this mode, but auto-replies and CAPI still work.',
            basic: 'Basic Information',
            internalName: 'Internal Name',
            internalPlaceholder: 'Example: Q2 Japan GTM Campaign',
            displayTitle: 'Display Title',
            displayPlaceholder: 'Example: Request Materials / Contact Us',
            externalDisplayNote: 'This is used for preview only and will not change your external website.',
            email: 'Email Delivery & Notifications',
            autoReply: 'Auto-Reply Email',
            autoReplyNote: 'SalesBreaker can send this auto-reply to the submitted email address, even from external website forms.',
            sender: 'Auto-Reply Sender',
            systemSender: 'System default (mailer@sbroute.net)',
            subjectPlaceholder: 'Subject: Thanks for contacting us',
            bodyPlaceholder: 'Body: Hi {{Name}}, ...',
            notifyTo: 'Notification Email',
            afterSubmit: 'After Submission',
            successMessage: 'Success Message',
            redirectUrl: 'Redirect URL (optional)',
            redirectNote: 'After an external form is submitted, visitors can be redirected to this URL.',
            design: 'Design & Appearance',
            themeColor: 'Theme Color',
            customColor: 'Custom Color (HEX)',
            buttonText: 'Submit Button Text',
            buttonPlaceholder: 'Example: Submit',
            fields: 'Form Fields',
            fieldLabel: 'Field Label',
            placeholderLabel: 'Placeholder',
            selectPlaceholderLabel: 'Default Select Text',
            optionPlaceholder: 'Example: Select an option',
            textPlaceholder: 'Example: Jane Smith',
            options: 'Options',
            applyFromPlaceholder: 'Use placeholder as options',
            delete: 'Delete',
            addOption: '+ Add Option',
            required: 'Required field',
            deleteFieldConfirm: 'Delete this field?',
            previewTitle: 'Form Title',
            previewInputSuffix: '...',
            externalActive: 'External Site Connected',
            cancel: 'Cancel',
            save: 'Save & Publish',
            submitSuccessDefault: 'Thank you. We will review your message and contact you shortly.',
            submitButtonDefault: 'Submit',
        },
        embed: {
            title: 'Installation & Integration Code',
            subtitle: 'Deployment & Integration',
            tagTab: 'Dedicated Tag',
            linkTab: 'Public URL',
            iframeTab: 'Embed',
            capiTab: 'CAPI SDK',
            tagDescription: 'Paste this dedicated auto-detection tag inside the <head> of your external website, WordPress site, or landing page. It detects form submissions on that page and applies this form configuration, including auto-replies.',
            tagLabel: 'One-Tag with Specific Token',
            copyTag: 'Copy Dedicated Tag',
            linkLabel: 'Public Access URL',
            copy: 'Copy',
            iframeLabel: 'HTML Embed Code',
            copyCode: 'Copy Code',
            capiDescription: 'Run this script after your custom landing page form succeeds to sync the data to SalesBreaker and support Meta CAPI optimization.',
            copySdk: 'Copy SDK Code',
            close: 'Close'
        }
    }
};

const getFormsCopy = (isEnglishBeta = false) => isEnglishBeta ? FORMS_COPY.en : FORMS_COPY.ja;

// --- Constants ---
const FIELD_TYPES = {
    ja: [
    { type: 'text', label: '1行テキスト', defaultLabel: 'テキスト項目' },
    { type: 'textarea', label: '複数行テキスト', defaultLabel: '詳細内容' },
    { type: 'email', label: 'メールアドレス', defaultLabel: 'メールアドレス' },
    { type: 'tel', label: '電話番号', defaultLabel: '電話番号' },
    { type: 'date', label: '日付', defaultLabel: '希望日' },
    { type: 'radio', label: 'ラジオボタン', defaultLabel: '選択してください', hasOptions: true },
    { type: 'checkbox', label: 'チェックボックス', defaultLabel: '選択してください', hasOptions: true },
    { type: 'select', label: 'プルダウン', defaultLabel: '選択してください', hasOptions: true },
    ],
    en: [
        { type: 'text', label: 'Short Text', defaultLabel: 'Text Field' },
        { type: 'textarea', label: 'Long Text', defaultLabel: 'Message' },
        { type: 'email', label: 'Email', defaultLabel: 'Email Address' },
        { type: 'tel', label: 'Phone', defaultLabel: 'Phone Number' },
        { type: 'date', label: 'Date', defaultLabel: 'Preferred Date' },
        { type: 'radio', label: 'Radio Buttons', defaultLabel: 'Select an option', hasOptions: true },
        { type: 'checkbox', label: 'Checkboxes', defaultLabel: 'Select options', hasOptions: true },
        { type: 'select', label: 'Dropdown', defaultLabel: 'Select an option', hasOptions: true },
    ]
};

const OPTION_FIELD_TYPES = new Set(['radio', 'checkbox', 'select']);
const DEFAULT_FIELD_OPTIONS = {
    ja: ['選択肢1', '選択肢2'],
    en: ['Option 1', 'Option 2']
};

const getLocaleKey = (isEnglishBeta = false) => isEnglishBeta ? 'en' : 'ja';

const getDefaultFieldOptions = (isEnglishBeta = false) => [...DEFAULT_FIELD_OPTIONS[getLocaleKey(isEnglishBeta)]];

const normalizeFieldOptions = (options, isEnglishBeta = false) => {
    if (!Array.isArray(options)) return getDefaultFieldOptions(isEnglishBeta);
    const cleaned = options.map(option => String(option || '').trim()).filter(Boolean);
    return cleaned.length > 0 ? cleaned : getDefaultFieldOptions(isEnglishBeta);
};

const parseOptionText = (text) => {
    return String(text || '')
        .split(/\r?\n|,|、/)
        .map(option => option.trim())
        .filter(Boolean);
};

const getDefaultFields = (isEnglishBeta = false) => isEnglishBeta ? [
    { id: 'company', type: 'text', label: 'Company', required: true, placeholder: 'Example Inc.' },
    { id: 'name', type: 'text', label: 'Name', required: true, placeholder: 'Jane Smith' },
    { id: 'email', type: 'email', label: 'Email Address', required: true, placeholder: 'info@example.com' },
    { id: 'phone', type: 'tel', label: 'Phone Number', required: false, placeholder: '+1 555 123 4567' },
    { id: 'message', type: 'textarea', label: 'Message', required: true, placeholder: 'Tell us what you are looking for.' },
] : [
    { id: 'company', type: 'text', label: '会社名', required: true, placeholder: '株式会社サンプル' },
    { id: 'name', type: 'text', label: 'お名前', required: true, placeholder: '山田 太郎' },
    { id: 'email', type: 'email', label: 'メールアドレス', required: true, placeholder: 'info@example.com' },
    { id: 'phone', type: 'tel', label: '電話番号', required: false, placeholder: '03-1234-5678' },
    { id: 'message', type: 'textarea', label: 'お問い合わせ内容', required: true, placeholder: 'ご質問をご記入ください' },
];

const COLOR_THEMES = [
    { id: 'blue', name: 'ブルー', primary: '#2563eb' },
    { id: 'green', name: 'グリーン', primary: '#059669' },
    { id: 'indigo', name: 'インディゴ', primary: '#4f46e5' },
    { id: 'rose', name: 'ローズ', primary: '#e11d48' },
    { id: 'black', name: 'ブラック', primary: '#111827' },
    { id: 'custom', name: 'カスタム', primary: '#6366f1' }, // ★追加
];

const COLOR_THEME_NAMES_EN = {
    blue: 'Blue',
    green: 'Green',
    indigo: 'Indigo',
    rose: 'Rose',
    black: 'Black',
    custom: 'Custom',
};

// --- Sub-components ---
const ConfigSection = ({ title, icon: Icon, colorClass, children, disabled = false }) => (
    <section className={`space-y-4 transition-all duration-300 ${disabled ? 'opacity-25 grayscale pointer-events-none scale-[0.98] origin-top' : ''}`}>
        <h4 className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest">
            <span className={`w-1.5 h-4 ${colorClass} rounded-full`}></span> {title}
        </h4>
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm space-y-5">
            {children}
        </div>
    </section>
);

// --- FormEditorModal 修正版 ---
function FormEditorModal({ form, issuerProfiles, onClose, onSave, isEnglishBeta = false }) {
    const { appName } = useBranding();
    const copy = getFormsCopy(isEnglishBeta).formEditor;
    const fieldTypes = FIELD_TYPES[getLocaleKey(isEnglishBeta)];
    const [formData, setFormData] = useState({
        title: '',
        display_title: '',
        is_external_api_mode: false,
        is_active: true,
        notification_email: '',
        is_auto_reply_enabled: true,
        issuer_profile_id: '',
        auto_reply_subject: '',
        auto_reply_body: '',
        success_message: copy.submitSuccessDefault,
        redirect_url: '',
        fields_config: getDefaultFields(isEnglishBeta),
        style_config: { 
            theme: 'blue', 
            button_text: copy.submitButtonDefault,
            border_radius: '8px',
            show_shadow: true
        }
    });
    
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);

    useEffect(() => {
        if (form && Object.keys(form).length > 0) {
            const parseJson = (val, fallback) => {
                if (!val) return fallback;
                return typeof val === 'string' ? JSON.parse(val) : val;
            };

            setFormData(prev => ({
                ...prev,
                ...form,
                fields_config: parseJson(form.fields_config, prev.fields_config),
                style_config: { ...prev.style_config, ...parseJson(form.style_config, {}) },
                is_external_api_mode: form.is_external_api_mode === 1 || form.is_external_api_mode === true,
                is_auto_reply_enabled: form.is_auto_reply_enabled !== 0,
                issuer_profile_id: form.issuer_profile_id ? String(form.issuer_profile_id) : ''
            }));
        }
    }, [form]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleStyleChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            style_config: { ...prev.style_config, [key]: value }
        }));
    };

    const handleModeToggle = (e) => {
        const checked = e.target.checked;
        setFormData(prev => ({
            ...prev,
            is_external_api_mode: checked,
            // 外部連携モード時でも自動返信は有効なままにできるよう変更
            is_auto_reply_enabled: prev.is_auto_reply_enabled 
        }));
    };

    // --- Field Operations (省略せず保持) ---
    const updateField = (index, updates) => {
        const newFields = [...formData.fields_config];
        newFields[index] = { ...newFields[index], ...updates };
        setFormData(prev => ({ ...prev, fields_config: newFields }));
    };

    const updateFieldOption = (fieldIndex, optionIndex, value) => {
        const field = formData.fields_config[fieldIndex];
        const options = Array.isArray(field.options) ? [...field.options] : getDefaultFieldOptions(isEnglishBeta);
        options[optionIndex] = value;
        updateField(fieldIndex, { options });
    };

    const addFieldOption = (fieldIndex) => {
        const field = formData.fields_config[fieldIndex];
        const options = Array.isArray(field.options) ? [...field.options] : getDefaultFieldOptions(isEnglishBeta);
        options.push(isEnglishBeta ? `Option ${options.length + 1}` : `選択肢${options.length + 1}`);
        updateField(fieldIndex, { options });
    };

    const removeFieldOption = (fieldIndex, optionIndex) => {
        const field = formData.fields_config[fieldIndex];
        const options = Array.isArray(field.options) ? [...field.options] : getDefaultFieldOptions(isEnglishBeta);
        if (options.length <= 1) return;
        updateField(fieldIndex, { options: options.filter((_, index) => index !== optionIndex) });
    };

    const applyPlaceholderAsOptions = (fieldIndex) => {
        const field = formData.fields_config[fieldIndex];
        const parsed = parseOptionText(field.placeholder);
        if (parsed.length === 0) return;
        updateField(fieldIndex, { options: parsed, placeholder: field.type === 'select' ? '' : field.placeholder });
    };

    const moveField = (index, direction) => {
        const newFields = [...formData.fields_config];
        const target = index + direction;
        if (target >= 0 && target < newFields.length) {
            [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
            setFormData(prev => ({ ...prev, fields_config: newFields }));
            if (editingFieldIndex === index) setEditingFieldIndex(target);
        }
    };

    const removeField = (index) => {
        if (!window.confirm(copy.deleteFieldConfirm)) return;
        const newFields = formData.fields_config.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, fields_config: newFields }));
        setEditingFieldIndex(null);
    };

    const addField = (typeConfig) => {
        const newField = {
            id: `field_${Date.now()}`,
            type: typeConfig.type,
            label: typeConfig.defaultLabel,
            required: false,
            placeholder: '',
            options: typeConfig.hasOptions ? getDefaultFieldOptions(isEnglishBeta) : undefined
        };
        const newFields = [...formData.fields_config, newField];
        setFormData(prev => ({ ...prev, fields_config: newFields }));
        setEditingFieldIndex(newFields.length - 1);
    };

    const currentThemeColor = useMemo(() => {
        if (formData.style_config.theme === 'custom' && formData.style_config.custom_color) {
            return formData.style_config.custom_color;
        }
        return COLOR_THEMES.find(t => t.id === formData.style_config.theme)?.primary || '#2563eb';
    }, [formData.style_config.theme, formData.style_config.custom_color]);

    const renderPreviewField = (field) => {
        const options = normalizeFieldOptions(field.options, isEnglishBeta);

        if (field.type === 'textarea') {
            return <div className="w-full h-32 bg-gray-50 rounded-2xl border border-gray-100" />;
        }

        if (field.type === 'select') {
            return (
                <div className="w-full h-14 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between px-5 text-gray-400 text-sm">
                    <span>{field.placeholder || copy.optionPlaceholder}</span>
                    <span className="text-gray-300">▼</span>
                </div>
            );
        }

        if (field.type === 'radio' || field.type === 'checkbox') {
            return (
                <div className="space-y-2">
                    {options.map((option, optionIndex) => (
                        <div key={`${field.id || field.label}_${optionIndex}`} className="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                            <span className={`${field.type === 'radio' ? 'rounded-full' : 'rounded'} block h-4 w-4 border-2 border-gray-200 bg-white`}></span>
                            <span className="text-sm text-gray-500">{option}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="w-full h-14 bg-gray-50 rounded-2xl border border-gray-100 flex items-center px-5 text-gray-300 text-sm">
                {field.placeholder || (isEnglishBeta ? `${field.label}${copy.previewInputSuffix}` : `${field.label}を入力...`)}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-md font-sans">
            <div className="bg-white w-full max-w-[98vw] lg:max-w-7xl rounded-2xl sm:rounded-[2.5rem] shadow-2xl h-[96vh] sm:h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                
                {/* Header */}
                <div className="flex justify-between items-center gap-4 px-5 py-4 sm:px-10 sm:py-6 border-b bg-white">
                    <div>
                        <h3 className="font-black text-xl sm:text-2xl text-gray-900 tracking-tight">
                            {form?.id ? copy.editTitle : copy.newTitle}
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                            {appName} Headless Engine
                        </p>
                    </div>
                    <button onClick={onClose} className="shrink-0 text-gray-300 hover:text-gray-600 transition-colors text-4xl leading-none">&times;</button>
                </div>
                
                <div className="flex-grow overflow-hidden flex flex-col md:flex-row bg-gray-50/50">
                    {/* Left Panel: Editor */}
                    <div className="w-full md:w-5/12 overflow-y-auto border-r p-4 sm:p-8 space-y-6 sm:space-y-10 pb-24 sm:pb-32 scrollbar-hide">
                        
                        {/* 外部連携モード切替 */}
                        <div className={`p-4 sm:p-6 rounded-3xl transition-all duration-500 shadow-xl border-b-4 flex items-start justify-between gap-4 sm:gap-6 ${formData.is_external_api_mode ? 'bg-indigo-600 border-indigo-900 text-white' : 'bg-white border-gray-100 text-gray-800'}`}>
                            <div className="flex-grow">
                                <h4 className="font-black text-sm uppercase tracking-widest flex items-center gap-2">
                                    <Icons.Code /> {copy.oneTagMode}
                                </h4>
                                <p className={`text-[10px] mt-2 leading-relaxed font-medium ${formData.is_external_api_mode ? 'text-indigo-100' : 'text-gray-400'}`}>
                                    {copy.oneTagBody}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                                <input type="checkbox" name="is_external_api_mode" checked={formData.is_external_api_mode} onChange={handleModeToggle} className="sr-only peer" />
                                <div className={`w-14 h-8 rounded-full transition-all peer-checked:bg-green-400 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all shadow-inner ${formData.is_external_api_mode ? 'bg-indigo-900' : 'bg-gray-200'}`}></div>
                            </label>
                        </div>

                        {/* 基本設定 */}
                        <ConfigSection title={copy.basic} colorClass="bg-blue-500">
                            <div className="grid grid-cols-1 gap-5">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.internalName}</label>
                                    <input type="text" name="title" required className="w-full p-3.5 border border-gray-100 rounded-2xl bg-gray-50/30 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all font-bold" value={formData.title} onChange={handleChange} placeholder={copy.internalPlaceholder} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.displayTitle}</label>
                                    <input type="text" name="display_title" className="w-full p-3.5 border border-gray-100 rounded-2xl bg-gray-50/30 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all font-bold" value={formData.display_title || ''} onChange={handleChange} placeholder={copy.displayPlaceholder} />
                                    {formData.is_external_api_mode && <p className="text-[9px] text-indigo-400 font-bold mt-1.5">{copy.externalDisplayNote}</p>}
                                </div>
                            </div>
                        </ConfigSection>

                        {/* 修正：外部連携モードでも「メール配信」と「完了後の挙動」を有効化 */}
                        
                        {/* メール配信設定 */}
                        <ConfigSection title={copy.email} colorClass="bg-indigo-500">
                            <label className="flex items-center justify-between p-4 bg-indigo-50 rounded-2xl cursor-pointer">
                                <div>
                                    <span className="text-sm font-black text-indigo-900">{copy.autoReply}</span>
                                    {formData.is_external_api_mode && <span className="ml-2 text-[9px] bg-indigo-200 text-indigo-700 px-2 py-0.5 rounded-full">Tag Active</span>}
                                </div>
                                <div className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" name="is_auto_reply_enabled" checked={formData.is_auto_reply_enabled} onChange={handleChange} className="sr-only peer" />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                                </div>
                            </label>
                            
                            {formData.is_auto_reply_enabled && (
                                <div className="space-y-4 pt-2 animate-in fade-in duration-300">
                                    <div className="p-3 bg-indigo-900 text-white rounded-xl text-[10px] font-bold leading-relaxed">
                                        {copy.autoReplyNote}
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-400 mb-1">{copy.sender}</label>
                                        <select name="issuer_profile_id" className="w-full p-3 border rounded-xl text-sm bg-white" value={formData.issuer_profile_id} onChange={handleChange}>
                                            <option value="">{copy.systemSender}</option>
                                            {issuerProfiles.map(p => <option key={p.id} value={p.id}>{p.sender_email} ({p.company_name})</option>)}
                                        </select>
                                    </div>
                                    <input type="text" name="auto_reply_subject" className="w-full p-3 border rounded-xl text-sm" value={formData.auto_reply_subject} onChange={handleChange} placeholder={copy.subjectPlaceholder} />
                                    <textarea name="auto_reply_body" className="w-full p-3 border rounded-xl text-sm h-32 leading-relaxed" value={formData.auto_reply_body} onChange={handleChange} placeholder={copy.bodyPlaceholder}></textarea>
                                </div>
                            )}
                            <div className="pt-2 border-t border-gray-100">
                                <label className="block text-[10px] font-black text-gray-400 mb-1">{copy.notifyTo}</label>
                                <input type="email" name="notification_email" className="w-full p-3 border rounded-xl text-sm" value={formData.notification_email} onChange={handleChange} placeholder="admin@example.com" />
                            </div>
                        </ConfigSection>

                        {/* 完了後設定 */}
                        <ConfigSection title={copy.afterSubmit} colorClass="bg-green-500">
                            <div className="space-y-4">
                                <div className={`transition-all ${formData.is_external_api_mode ? 'opacity-20 pointer-events-none' : ''}`}>
                                    <label className="block text-[10px] font-black text-gray-400 mb-1">{copy.successMessage}</label>
                                    <textarea name="success_message" className="w-full p-3 border rounded-xl text-sm h-20" value={formData.success_message} onChange={handleChange}></textarea>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 mb-1">{copy.redirectUrl}</label>
                                    <div className="p-2 bg-green-50 text-green-700 text-[10px] font-bold rounded-lg mb-2">
                                        {copy.redirectNote}
                                    </div>
                                    <input type="url" name="redirect_url" className="w-full p-3 border rounded-xl text-sm" value={formData.redirect_url} onChange={handleChange} placeholder="https://example.com/thanks" />
                                </div>
                            </div>
                        </ConfigSection>

                        {/* デザインと項目は外部連携モードなら無効でOK */}
                        <ConfigSection title={copy.design} colorClass="bg-pink-500" disabled={formData.is_external_api_mode}>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 mb-3 tracking-widest uppercase">{copy.themeColor}</label>
                                    <div className="flex flex-wrap gap-3">
                                        {COLOR_THEMES.map(t => (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => handleStyleChange('theme', t.id)}
                                                className={`w-10 h-10 rounded-full border-4 transition-all ${formData.style_config.theme === t.id ? 'border-blue-100 scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                                style={{ backgroundColor: t.primary }}
                                                title={isEnglishBeta ? COLOR_THEME_NAMES_EN[t.id] : t.name}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {formData.style_config.theme === 'custom' && (
                                    <div className="p-4 bg-gray-50 rounded-2xl animate-in slide-in-from-top-2 duration-300">
                                        <label className="block text-[10px] font-black text-gray-400 mb-2">{copy.customColor}</label>
                                        <div className="flex gap-3">
                                            <input type="color" className="w-12 h-12 rounded-lg cursor-pointer" value={formData.style_config.custom_color || '#6366f1'} onChange={(e) => handleStyleChange('custom_color', e.target.value)} />
                                            <input type="text" className="flex-grow p-3 border rounded-xl text-sm font-mono uppercase" value={formData.style_config.custom_color || '#6366f1'} onChange={(e) => handleStyleChange('custom_color', e.target.value)} />
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 mb-2">{copy.buttonText}</label>
                                    <input type="text" className="w-full p-4 border border-gray-100 rounded-2xl bg-gray-50/30 focus:bg-white transition-all font-bold" value={formData.style_config.button_text} onChange={(e) => handleStyleChange('button_text', e.target.value)} placeholder={copy.buttonPlaceholder} />
                                </div>
                            </div>
                        </ConfigSection>
                        <ConfigSection title={copy.fields} colorClass="bg-amber-500" disabled={formData.is_external_api_mode}>
                            <div className="space-y-6">
                                <div className="flex flex-wrap gap-2">
                                    {fieldTypes.map(ft => (
                                        <button
                                            key={ft.type}
                                            type="button"
                                            onClick={() => addField(ft)}
                                            className="px-4 py-2 bg-gray-900 text-white rounded-full text-[10px] font-black hover:bg-blue-600 transition-colors"
                                        >
                                            + {ft.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="space-y-3">
                                    {formData.fields_config.map((field, idx) => (
                                        <div key={field.id} className={`p-4 rounded-2xl border transition-all ${editingFieldIndex === idx ? 'border-blue-400 bg-blue-50/30 shadow-md' : 'border-gray-100 bg-white'}`}>
                                            <div className="flex justify-between items-start gap-3 sm:items-center sm:gap-4">
                                                <div className="flex min-w-0 items-center gap-3 overflow-hidden pt-1 sm:pt-0">
                                                    <span className="text-[10px] font-black text-gray-300 w-4">{idx + 1}</span>
                                                    <span className="text-xs font-bold text-gray-700 truncate">{field.label}</span>
                                                </div>
                                                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                                                    <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="p-1.5 text-gray-400 hover:text-gray-900 disabled:opacity-0"><Icons.Up /></button>
                                                    <button onClick={() => moveField(idx, 1)} disabled={idx === formData.fields_config.length - 1} className="p-1.5 text-gray-400 hover:text-gray-900 disabled:opacity-0"><Icons.Down /></button>
                                                    {/* ★ 編集ボタンを追加 */}
                                                    <button onClick={() => setEditingFieldIndex(editingFieldIndex === idx ? null : idx)} className={`p-1.5 transition-colors ${editingFieldIndex === idx ? 'text-blue-600' : 'text-gray-400 hover:text-blue-600'}`}><Icons.Edit /></button>
                                                    <button onClick={() => removeField(idx)} className="p-1.5 text-gray-400 hover:text-red-500"><Icons.Trash /></button>
                                                </div>
                                            </div>

                                            {/* ★ 詳細編集パネル (アコーディオン) を復元 */}
                                            {editingFieldIndex === idx && (
                                                <div className="mt-4 pt-4 border-t border-blue-100 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                                                    <div>
                                                        <label className="block text-[9px] font-black text-blue-400 uppercase mb-1">{copy.fieldLabel}</label>
                                                        <input type="text" className="w-full p-2.5 bg-white border border-blue-100 rounded-xl text-xs font-bold" value={field.label} onChange={(e) => updateField(idx, { label: e.target.value })} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[9px] font-black text-blue-400 uppercase mb-1">{field.type === 'select' ? copy.selectPlaceholderLabel : copy.placeholderLabel}</label>
                                                        <input type="text" className="w-full p-2.5 bg-white border border-blue-100 rounded-xl text-xs" value={field.placeholder || ''} onChange={(e) => updateField(idx, { placeholder: e.target.value })} placeholder={OPTION_FIELD_TYPES.has(field.type) ? copy.optionPlaceholder : copy.textPlaceholder} />
                                                    </div>
                                                    {OPTION_FIELD_TYPES.has(field.type) && (
                                                        <div className="rounded-2xl bg-white border border-blue-100 p-3 space-y-3">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <label className="block text-[9px] font-black text-blue-400 uppercase">{copy.options}</label>
                                                                {parseOptionText(field.placeholder).length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => applyPlaceholderAsOptions(idx)}
                                                                        className="rounded-full bg-indigo-50 px-3 py-1.5 text-[9px] font-black text-indigo-600 hover:bg-indigo-100"
                                                                    >
                                                                        {copy.applyFromPlaceholder}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="space-y-2">
                                                                {normalizeFieldOptions(field.options, isEnglishBeta).map((option, optionIndex) => (
                                                                    <div key={`${field.id}_option_${optionIndex}`} className="flex items-center gap-2">
                                                                        <input
                                                                            type="text"
                                                                            className="min-w-0 flex-1 p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold focus:bg-white focus:border-blue-200 outline-none"
                                                                            value={option}
                                                                            onChange={(e) => updateFieldOption(idx, optionIndex, e.target.value)}
                                                                            placeholder={isEnglishBeta ? `Option ${optionIndex + 1}` : `選択肢${optionIndex + 1}`}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeFieldOption(idx, optionIndex)}
                                                                            disabled={normalizeFieldOptions(field.options, isEnglishBeta).length <= 1}
                                                                            className="shrink-0 rounded-full px-3 py-2 text-[9px] font-black text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                                                                        >
                                                                            {copy.delete}
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => addFieldOption(idx)}
                                                                className="w-full rounded-xl border border-dashed border-blue-200 bg-blue-50/50 py-2.5 text-[10px] font-black text-blue-600 hover:bg-blue-50"
                                                            >
                                                                {copy.addOption}
                                                            </button>
                                                        </div>
                                                    )}
                                                    <label className="flex items-center gap-2 cursor-pointer group">
                                                        <input type="checkbox" className="w-4 h-4 rounded border-blue-200 text-blue-600 focus:ring-blue-500" checked={field.required} onChange={(e) => updateField(idx, { required: e.target.checked })} />
                                                        <span className="text-[10px] font-bold text-gray-500 group-hover:text-blue-600">{copy.required}</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ConfigSection>

                    </div>

                    <div className="hidden md:flex w-full md:w-7/12 bg-gray-100 p-8 lg:p-16 items-start justify-center overflow-y-auto relative scrollbar-hide">
                        {/* プレビュー本体 */}
                        <div className="w-full max-w-md bg-white rounded-[2.5rem] overflow-hidden shadow-2xl transition-all duration-500">
                            {/* ヘッダー部分 */}
                            <div className="p-10 text-center text-white relative" style={{ backgroundColor: currentThemeColor }}>
                                <h2 className="text-2xl font-black relative z-10">{formData.display_title || copy.previewTitle}</h2>
                                <div className="absolute inset-0 bg-black/5"></div>
                            </div>

                            {/* フォーム入力欄のシミュレーション */}
                            <div className="p-10 space-y-8">
                                {formData.fields_config.map((field, idx) => (
                                    <div key={idx} className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                                            {field.label} {field.required && <span className="text-red-500">*</span>}
                                        </label>
                                        {renderPreviewField(field)}
                                    </div>
                                ))}

                                {/* 送信ボタン */}
                                <button 
                                    type="button" 
                                    className="w-full py-5 text-white font-black text-sm shadow-xl transition-all uppercase tracking-[0.2em]"
                                    style={{ 
                                        backgroundColor: currentThemeColor, 
                                        borderRadius: formData.style_config.border_radius || '12px' 
                                    }}
                                >
                                    {formData.style_config.button_text}
                                </button>
                                
                                <p className="text-center text-[10px] text-gray-300 font-bold uppercase tracking-widest mt-4">
                                    Powered by {appName}
                                </p>
                            </div>
                        </div>

                        {/* 外部連携中のオーバーレイ通知 */}
                        {formData.is_external_api_mode && (
                            <div className="absolute top-8 right-8 bg-indigo-600 text-white px-6 py-3 rounded-full text-xs font-black shadow-2xl animate-pulse flex items-center gap-2">
                                <Icons.Code /> {copy.externalActive}
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer (省略) */}
                <div className="p-4 sm:px-12 sm:py-6 border-t flex flex-col-reverse gap-3 bg-white z-10 shadow-[0_-10px_40px_rgba(0,0,0,0.03)] sm:flex-row sm:justify-end sm:items-center sm:gap-6">
                    <button onClick={onClose} className="w-full rounded-full bg-gray-50 px-4 py-3 text-xs font-black text-gray-400 hover:text-gray-900 transition-colors tracking-widest uppercase sm:w-auto sm:bg-transparent sm:px-0 sm:py-0">{copy.cancel}</button>
                    <button onClick={(e) => { e.preventDefault(); onSave(formData); }} className="w-full px-4 py-3 sm:w-auto sm:px-16 sm:py-4 bg-blue-600 text-white rounded-full font-black text-xs sm:text-sm shadow-2xl shadow-blue-200 hover:bg-blue-700 hover:scale-105 active:scale-95 transition-all">
                        {copy.save}
                    </button>
                </div>
            </div>
        </div>
    );
}

function EmbedCodeModal({ form, onClose, isEnglishBeta = false }) {
    const { appName } = useBranding();
    const copy = getFormsCopy(isEnglishBeta);
    const embedCopy = copy.embed;
    const { clientId } = useOutletContext(); // 認証コンテキストからIDを取得
    const [activeTab, setActiveTab] = useState('tag'); // 初期タブを 'tag' (専用タグ) に設定
    
    const formUrl = `${window.location.origin}/app/public/form/${form.public_token}`;
    const iframeCode = `<iframe src="${formUrl}" width="100%" height="800" frameborder="0" scrolling="auto"></iframe>`;
    
    // このフォーム専用のワンタグ（自動検知用）
    const oneTagSpecific = `<script src="${API_URL}/v1/sb-track.js?id=${clientId}&token=${form.public_token}" async></script>`;

    const capiScript = `
<script>
/**
 * ${isEnglishBeta ? 'SalesBreaker external form integration (analytics + CAPI)' : 'Sales Breaker 外部フォーム連携 (分析・CAPI強化版)'}
 */
function reportToSalesBreaker(formData) {
  var endpoint = "${API_URL}/api/public/form/${form.public_token}/submit";
  
  // ${isEnglishBeta ? 'Helper for reading advertising attribution cookies' : 'クッキーから広告情報を取得するためのヘルパー'}
  var getSB = function(n){var a="; "+document.cookie,b=a.split("; sb_"+n+"=");return 2==b.length?b.pop().split(";").shift():null};

  var payload = {
    data: formData,
    visitorId: (function(n){var a="; "+document.cookie,b=a.split("; "+n+"=");return 2==b.length?b.pop().split(";").shift():null})("sb_vid"),
    utm_source: getSB("utm_source"),
    utm_campaign: getSB("utm_campaign"),
    utm_medium: getSB("utm_medium"),
    utm_content: getSB("utm_content"),
    fb_ad_id: getSB("fb_ad_id")
  };

  fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  })
  .then(function(res) { return res.json(); })
  .then(function(data) { console.log("SB Synced:", data); })
  .catch(function(err) { console.error("SB Sync Error:", err); });
}
</script>`.trim();

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        // 必要に応じてここでトースト通知などを出す
        alert(copy.copied);
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-3xl rounded-3xl sm:rounded-[3rem] shadow-2xl p-5 sm:p-12 max-h-[92vh] overflow-y-auto animate-in zoom-in-95 duration-300">
                <div className="flex justify-between items-center gap-4 mb-6 sm:mb-10">
                    <div>
                        <h3 className="font-black text-2xl sm:text-3xl text-gray-900 tracking-tight">{embedCopy.title}</h3>
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-[0.2em] mt-2">{embedCopy.subtitle}</p>
                    </div>
                    <button onClick={onClose} className="shrink-0 text-gray-200 hover:text-gray-400 text-4xl sm:text-5xl leading-none transition-colors">&times;</button>
                </div>
                
                <div className="flex border-b border-gray-100 mb-6 sm:mb-10 overflow-x-auto scrollbar-hide">
                    {/* 専用タグタブ（追加） */}
                    <button onClick={() => setActiveTab('tag')} className={`shrink-0 px-5 sm:px-8 py-4 font-black text-xs uppercase tracking-widest border-b-4 transition-all ${activeTab === 'tag' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-300'}`}>{embedCopy.tagTab}</button>
                    
                    {!form.is_external_api_mode && (
                        <>
                            <button onClick={() => setActiveTab('link')} className={`shrink-0 px-5 sm:px-8 py-4 font-black text-xs uppercase tracking-widest border-b-4 transition-all ${activeTab === 'link' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-300'}`}>{embedCopy.linkTab}</button>
                            <button onClick={() => setActiveTab('iframe')} className={`shrink-0 px-5 sm:px-8 py-4 font-black text-xs uppercase tracking-widest border-b-4 transition-all ${activeTab === 'iframe' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-300'}`}>{embedCopy.iframeTab}</button>
                        </>
                    )}
                    <button onClick={() => setActiveTab('capi')} className={`shrink-0 px-5 sm:px-8 py-4 font-black text-xs uppercase tracking-widest border-b-4 transition-all ${activeTab === 'capi' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-300'}`}>{embedCopy.capiTab}</button>
                </div>
                
                <div className="space-y-8 min-h-[250px]">
                    {/* 専用タグの中身（追加） */}
                    {activeTab === 'tag' && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <div className="bg-indigo-50 p-4 sm:p-6 rounded-3xl border border-indigo-100 text-[11px] text-indigo-900 font-bold leading-relaxed">
                                {embedCopy.tagDescription}
                            </div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest font-sans">{embedCopy.tagLabel}</p>
                            <textarea readOnly value={oneTagSpecific} className="w-full p-4 sm:p-6 border border-gray-100 rounded-3xl bg-gray-900 text-indigo-300 font-mono text-xs h-24 leading-relaxed shadow-inner" />
                            <button onClick={() => handleCopy(oneTagSpecific)} className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all uppercase tracking-widest">{embedCopy.copyTag}</button>
                        </div>
                    )}

                    {activeTab === 'link' && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest font-sans">{embedCopy.linkLabel}</p>
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <input type="text" readOnly value={formUrl} className="min-w-0 flex-grow p-4 sm:p-5 border border-gray-100 rounded-3xl bg-gray-50 font-mono text-sm" />
                                <button onClick={() => handleCopy(formUrl)} className="px-10 py-4 sm:py-0 bg-blue-600 text-white rounded-3xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all">{embedCopy.copy}</button>
                            </div>
                        </div>
                    )}
                    {activeTab === 'iframe' && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest font-sans">{embedCopy.iframeLabel}</p>
                            <textarea readOnly value={iframeCode} className="w-full p-4 sm:p-6 border border-gray-100 rounded-3xl bg-gray-900 text-green-400 font-mono text-xs h-32 leading-relaxed" />
                            <button onClick={() => handleCopy(iframeCode)} className="w-full py-5 bg-gray-800 text-white rounded-3xl font-black shadow-xl hover:bg-gray-700 transition-all">{embedCopy.copyCode}</button>
                        </div>
                    )}
                    {activeTab === 'capi' && (
                        <div className="space-y-4 animate-in fade-in duration-300">
                            <div className="bg-indigo-50 p-4 sm:p-6 rounded-3xl border border-indigo-100 text-[11px] text-indigo-900 font-bold leading-relaxed">
                                {isEnglishBeta ? embedCopy.capiDescription : `自作LPのフォーム成功時にこのスクリプトを実行することで、${appName}へのデータ同期と Meta CAPI の広告最適化が同時に行われます。`}
                            </div>
                            <textarea readOnly value={capiScript} className="w-full p-4 sm:p-6 border border-gray-100 rounded-3xl bg-gray-900 text-indigo-300 font-mono text-xs h-48 leading-relaxed shadow-inner" />
                            <button onClick={() => handleCopy(capiScript)} className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all uppercase tracking-widest">{embedCopy.copySdk}</button>
                        </div>
                    )}
                </div>
                
                <div className="mt-8 sm:mt-12 flex justify-end">
                    <button onClick={onClose} className="w-full px-12 py-4 bg-gray-50 text-gray-400 rounded-full font-black text-xs hover:bg-gray-100 transition-all uppercase tracking-widest sm:w-auto">{embedCopy.close}</button>
                </div>
            </div>
        </div>
    );
}

export default function MarketingFormsPage() {
    const { clientId } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = getFormsCopy(isEnglishBeta);
    const defaultFields = useMemo(() => getDefaultFields(isEnglishBeta), [isEnglishBeta]);
    const [state, setState] = useState({
        forms: [],
        issuerProfiles: [],
        userPlan: 'free',
        isLoading: true,
        editingForm: null,
        embedForm: null
    });

    const fetchData = useCallback(async () => {
        if (!clientId) return;
        setState(s => ({ ...s, isLoading: true }));
        try {
            const [formsRes, profilesRes] = await Promise.all([
                fetch(`${API_URL}/api/marketing/forms?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/issuer-profiles?client_id=${clientId}`, { headers: getAuthHeader() })
            ]);
            const [fData, pData] = await Promise.all([formsRes.json(), profilesRes.json()]);
            
            setState(s => ({ 
                ...s, 
                isLoading: false, 
                forms: fData.success ? fData.forms : [],
                userPlan: fData.plan || 'free',
                issuerProfiles: pData.success ? (pData.profiles || []) : []
            }));
        } catch (err) {
            console.error(err);
            setState(s => ({ ...s, isLoading: false }));
        }
    }, [clientId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSave = async (formData) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/forms`, {
                method: "POST",
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({ ...formData, client_id: clientId })
            });
            const data = await res.json();
            if (data.success) {
                setState(s => ({ ...s, editingForm: null }));
                fetchData();
            } else {
                throw new Error(data.error);
            }
        } catch (err) { 
            console.error(err.message); 
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(copy.deleteConfirm)) return;
        try {
            const res = await fetch(`${API_URL}/api/marketing/forms/${id}`, {
                method: "DELETE",
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({ client_id: clientId })
            });
            if (res.ok) fetchData();
        } catch (e) { console.error(copy.deleteFailed); }
    };

    return (
        <div className="p-3 sm:p-6 lg:p-12 bg-gray-50/30 min-h-full font-sans selection:bg-blue-100">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8 mb-10 sm:mb-20">
                    <div>
                        <h1 className="text-2xl sm:text-5xl font-black text-gray-900 tracking-tight">{copy.pageTitle}</h1>
                        <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4 font-bold uppercase tracking-[0.18em] sm:tracking-[0.3em]">{copy.pageSubtitle}</p>
                    </div>
                    <button 
                        onClick={() => {
                            if (state.userPlan === 'free' && state.forms.length >= 1) {
                                alert(copy.freeLimit);
                                return;
                            }
                            setState(s => ({ ...s, editingForm: { fields_config: defaultFields } }));
                        }}
                        className="flex w-full items-center justify-center gap-4 bg-blue-600 text-white font-black py-4 sm:py-5 px-8 sm:px-14 rounded-full hover:bg-blue-700 shadow-[0_25px_60px_-15px_rgba(37,99,235,0.4)] transition-all hover:-translate-y-1.5 active:scale-95 group md:w-auto"
                    >
                        <Icons.Plus /> <span>{copy.createButton}</span>
                    </button>
                </div>

                {state.isLoading ? (
                    <div className="bg-white rounded-3xl sm:rounded-[3.5rem] p-8 sm:p-20 lg:p-32 shadow-sm border border-gray-50 flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-blue-600 mb-8 sm:mb-10"></div>
                        <p className="text-gray-300 font-black tracking-[0.4em] uppercase text-[10px]">{copy.loading}</p>
                    </div>
                ) : state.forms.length === 0 ? (
                    <div className="bg-white rounded-3xl sm:rounded-[3.5rem] p-6 sm:p-20 lg:p-32 shadow-sm border-4 border-dashed border-gray-50 flex flex-col items-center text-center">
                        <div className="bg-gray-50 p-6 sm:p-10 rounded-full mb-6 sm:mb-10 text-gray-300"><Icons.Plus /></div>
                        <h2 className="text-xl sm:text-4xl font-black text-gray-900 mb-4 sm:mb-6">{copy.emptyTitle}</h2>
                        <p className="text-sm sm:text-base text-gray-400 max-w-sm mb-10 sm:mb-14 leading-relaxed font-medium">{copy.emptyBody}</p>
                        <button onClick={() => setState(s => ({ ...s, editingForm: { fields_config: defaultFields } }))} className="text-blue-600 font-black hover:underline tracking-widest uppercase text-xs">{copy.emptyCta}</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-12">
                        {state.forms.map(form => (
                            <div key={form.id} className="bg-white rounded-3xl sm:rounded-[3rem] shadow-sm border border-gray-50/50 hover:shadow-[0_50px_100px_-20px_rgba(0,0,0,0.1)] hover:-translate-y-4 transition-all duration-700 group overflow-hidden">
                                <div className="p-6 sm:p-12">
                                    <div className="flex justify-between items-start mb-10">
                                        <div className="flex flex-wrap gap-2 sm:gap-3">
                                            <span className={`px-6 py-2 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm ${form.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                {form.is_active ? 'Live' : 'Draft'}
                                            </span>
                                            {Boolean(form.is_external_api_mode) && (
                                                <span className="px-6 py-2 bg-indigo-50 text-indigo-600 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm">
                                                    <Icons.Code /> CAPI
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex gap-2 opacity-100 transition-all duration-500 sm:opacity-0 sm:group-hover:opacity-100 sm:translate-x-4 sm:group-hover:translate-x-0">
                                            <button onClick={() => setState(s => ({ ...s, editingForm: form }))} className="p-3.5 text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"><Icons.Edit /></button>
                                            <button onClick={() => handleDelete(form.id)} className="p-3.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"><Icons.Trash /></button>
                                        </div>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-3 break-words group-hover:text-blue-600 transition-colors">{form.title}</h3>
                                    <p className="text-[10px] text-gray-400 mb-8 sm:mb-14 font-bold uppercase tracking-widest leading-relaxed">
                                        {form.is_external_api_mode ? copy.externalModeLabel : (form.display_title || copy.unnamedForm)}
                                    </p>
                                    
                                    <div className="flex items-center gap-4 pt-8 sm:pt-10 border-t border-gray-50">
                                        <button onClick={() => setState(s => ({ ...s, embedForm: form }))} className="flex-grow flex items-center justify-center gap-3 bg-gray-900 text-white text-[10px] font-black py-5 rounded-2xl hover:bg-indigo-600 shadow-2xl shadow-gray-100 transition-all uppercase tracking-[0.2em]">
                                            <Icons.Code /> {copy.integration}
                                        </button>
                                        <a href={`${window.location.origin}/app/public/form/${form.public_token}`} target="_blank" rel="noopener noreferrer" className="p-5 bg-gray-50 text-gray-300 rounded-2xl hover:bg-blue-50 hover:text-blue-500 transition-all border border-transparent hover:border-blue-100">
                                            <Icons.Eye />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {state.editingForm && (
                <FormEditorModal 
                    form={state.editingForm} 
                    issuerProfiles={state.issuerProfiles} 
                    onClose={() => setState(s => ({ ...s, editingForm: null }))} 
                    onSave={handleSave}
                    isEnglishBeta={isEnglishBeta}
                />
            )}
            {state.embedForm && (
                <EmbedCodeModal 
                    form={state.embedForm} 
                    onClose={() => setState(s => ({ ...s, embedForm: null }))}
                    isEnglishBeta={isEnglishBeta}
                />
            )}

            <style>{`
            @keyframes shimmer {
                0% { left: -100%; opacity: 0; }
                30% { left: 100%; opacity: 1; }
                100% { left: 100%; opacity: 0; }
            }
            `}</style>
        </div>
    );
}
